import { HubConnection, HubConnectionBuilder, HttpTransportType, LogLevel } from '@microsoft/signalr';
import { apiConfig } from '../config/authConfig';

interface NegotiateResponse {
  connectionId: string;
  url: string;
  accessToken: string;
}

export interface ProgressUpdate {
  type: 'progress';
  connectionId: string;
  correlationId?: string;
  stage: string;
  progress: number;
  message: string;
  timestamp: string;
}

export type ProgressCallback = (update: ProgressUpdate) => void;

class SignalRService {
  private connection: HubConnection | null = null;
  private connectionId = '';
  private progressCallbacks: Map<string, ProgressCallback> = new Map();

  async connect(): Promise<string> {
    if (this.connection?.state === 'Connected') {
      return this.connectionId;
    }

    try {
      // Get auth token for negotiate request
      const authToken = await this.getAccessToken();
      
      // Call our backend's negotiate endpoint to get SignalR connection info
      const negotiateResponse = await this.negotiate(authToken);
      
      if (!negotiateResponse) {
        throw new Error('Failed to negotiate SignalR connection');
      }
      
      this.connectionId = negotiateResponse.connectionId;
      
      // For Azure SignalR Serverless mode, we must use skipNegotiation=true
      // The backend already performed negotiate and gave us the URL + access token
      // We connect directly to the SignalR service with WebSockets
      this.connection = new HubConnectionBuilder()
        .withUrl(negotiateResponse.url, {
          accessTokenFactory: () => negotiateResponse.accessToken,
          skipNegotiation: true,
          transport: HttpTransportType.WebSockets
        })
        .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
        .configureLogging(LogLevel.Information)
        .build();

      // Set up event handlers
      this.connection.on('progress', (data: ProgressUpdate) => {
        console.log('SignalR progress update:', data);
        
        // Call all registered callbacks
        this.progressCallbacks.forEach(callback => {
          try {
            callback(data);
          } catch (error) {
            console.error('Error in progress callback:', error);
          }
        });
      });

      this.connection.onreconnecting(() => {
        console.log('SignalR reconnecting...');
      });

      this.connection.onreconnected(() => {
        console.log('SignalR reconnected');
      });

      this.connection.onclose((error) => {
        if (error) {
          console.warn('SignalR connection closed with error:', error);
        } else {
          console.log('SignalR connection closed gracefully');
        }
      });

      await this.connection.start();
      console.log('SignalR connected with connection ID:', this.connectionId);

      return this.connectionId;
    } catch (error) {
      // Log at warn level since app continues working via polling
      console.warn('SignalR real-time connection not available, using polling fallback:', 
        error instanceof Error ? error.message : 'Connection failed');
      // Return fallback ID so app continues working without real-time updates
      this.connectionId = `fallback-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      return this.connectionId;
    }
  }

  async disconnect(): Promise<void> {
    if (this.connection) {
      await this.connection.stop();
      this.connection = null;
      this.connectionId = '';
      this.progressCallbacks.clear();
    }
  }

  registerProgressCallback(key: string, callback: ProgressCallback): void {
    this.progressCallbacks.set(key, callback);
  }

  unregisterProgressCallback(key: string): void {
    this.progressCallbacks.delete(key);
  }

  getConnectionId(): string {
    return this.connectionId;
  }

  isConnected(): boolean {
    return this.connection?.state === 'Connected';
  }

  private async negotiate(authToken: string): Promise<NegotiateResponse | null> {
    // Call our backend's negotiate endpoint to get SignalR connection info
    try {
      const negotiateUrl = `${apiConfig.baseUrl}/signalr/negotiate`;
      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      };
      
      // Add auth header if we have a real token
      if (authToken && authToken !== 'pottery-catalog-token') {
        headers['Authorization'] = `Bearer ${authToken}`;
      }
      
      const response = await fetch(negotiateUrl, {
        method: 'POST',
        headers,
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error(`Negotiate failed: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('SignalR negotiate response:', {
        connectionId: data.connectionId,
        url: data.url,
        hasAccessToken: !!data.accessToken
      });
      
      return {
        connectionId: data.connectionId,
        url: data.url,
        accessToken: data.accessToken
      };
    } catch (error) {
      console.error('SignalR negotiate failed:', error);
      return null;
    }
  }

  private async getAccessToken(): Promise<string> {
    // Get the access token from MSAL for proper Azure AD authentication
    try {
      // Import MSAL instance from your auth configuration
      const msalInstance = (window as any).msalInstance; // eslint-disable-line @typescript-eslint/no-explicit-any
      
      if (!msalInstance) {
        console.warn('MSAL instance not found, using fallback token');
        return 'pottery-catalog-token';
      }

      const accounts = msalInstance.getAllAccounts();
      if (accounts.length === 0) {
        console.warn('No authenticated accounts found, using fallback token');
        return 'pottery-catalog-token';
      }

      // Request token silently with basic scopes for multi-tenant support
      // Custom API scopes don't work for consumer Microsoft accounts
      const tokenResponse = await msalInstance.acquireTokenSilent({
        scopes: ['openid', 'profile', 'email', 'User.Read'],
        account: accounts[0]
      });
      // Use idToken for backend API calls - backend validates token audience as client_id
      return tokenResponse.idToken || tokenResponse.accessToken;
    } catch (error) {
      console.error('Failed to acquire access token:', error);
      
      // Try interactive authentication as fallback with basic scopes
      try {
        const msalInstance = (window as any).msalInstance; // eslint-disable-line @typescript-eslint/no-explicit-any
        if (msalInstance) {
          const tokenResponse = await msalInstance.acquireTokenPopup({
            scopes: ['openid', 'profile', 'email', 'User.Read']
          });
          // Use idToken for backend API calls
          return tokenResponse.idToken || tokenResponse.accessToken;
        }
      } catch (interactiveError) {
        console.error('Interactive authentication failed:', interactiveError);
      }
      
      // Fallback for development
      return 'pottery-catalog-token';
    }
  }
}

// Export a singleton instance
export const signalRService = new SignalRService();
export default signalRService;